import Konva from "konva";
import { Context } from "konva/lib/Context";
import {
    BORDER_OPA_COVER,
    BORDER_SIDE_FULL,
    appendRingPath,
    appendRoundedRect,
    effectiveRadius,
    resolveBorderGeometry,
} from "./borderGeometry";
import {
    OUTLINE_COLOR_DEFAULT,
    appendOutlinePath,
    resolveOutlineGeometry,
} from "./outlineGeometry";
import { resolveBackgroundGradient, type CanvasGradientAxis } from "./backgroundGradient";

export interface CustomRectConfig extends Konva.ShapeConfig {
    cornerRadius?: number;
    borderWidth?: number;
    borderSide?: number;
    borderOpa?: number;
    /** LVGL outline_width — 0 (the default) draws no outline at all. */
    outlineWidth?: number | null;
    outlineColor?: string | null;
    /** LVGL outline_opa, 0-255. Unset resolves to LV_OPA_COVER. */
    outlineOpa?: number | null;
    /** LVGL outline_pad — the gap between the object edge and the outline. */
    outlinePad?: number | null;
    /** Draw only the outline ring (used by obj's post-children render pass). */
    outlineOnly?: boolean;
    /** Suppress the outline while another render pass owns its draw order. */
    drawOutline?: boolean;
}

/**
 * CustomRect - A Konva Shape that draws borders INSIDE the shape bounds,
 * matching LVGL behavior (unlike standard Konva.Rect which draws borders
 * half-inside, half-outside).
 *
 * The border is a single even-odd FILL of the ring between the outer and inner
 * rounded rects — the same construction LVGL 7.11's draw_border() uses — not a
 * stroke. See borderGeometry.ts for why, and for how border_side reshapes the
 * inner rect. One fill means a translucent border stays uniform where sides
 * meet, and partial sides need no per-widget code.
 *
 * It also draws the LVGL outline — a closed ring OUTSIDE the object, painted
 * before the background (see outlineGeometry.ts). The outline is pure visual
 * overflow: getSelfRect()/getClientRect() below stay the object's own box, so
 * selection, the Transformer, snapping and export are untouched by it.
 */
export default class CustomRect extends Konva.Shape<CustomRectConfig> {
    private _cornerRadius: number;
    private _borderWidth: number;
    private _borderSide: number;
    private _borderOpa: number;
    private _outlineWidth: number | null;
    private _outlineColor: string | null;
    private _outlineOpa: number | null;
    private _outlinePad: number | null;
    private _outlineOnly: boolean;
    private _drawOutlineEnabled: boolean;
    private _backgroundGradientConfig: {
        axis: CanvasGradientAxis;
        mainColor: string;
        gradientColor?: string;
        mainStop: number;
        gradientStop: number;
    } | null = null;

    constructor(config: CustomRectConfig) {
        // Ensure strokeWidth is 0 so Konva doesn't add it to bounding box
        super({
            ...config,
            strokeWidth: 0,
            hitStrokeWidth: 0,
            perfectDrawEnabled: false, // Disable pixel-perfect drawing for better performance
        });
        // No Konva shadow attribute is ever set on this shape: the LVGL shadow
        // is a separate LvglShadowShape drawn behind it (see shadowGeometry.ts).
        // Konva's Shape._hasShadow() requires one of shadowColor/shadowBlur/
        // shadowOffsetX/shadowOffsetY to be truthy, so with none of them set
        // neither the fill nor the border can cast a native Gaussian shadow,
        // and shadowForStrokeEnabled is never consulted. Setting shadow
        // attributes here would reintroduce exactly that double shadow.

        this._cornerRadius = config.cornerRadius || 0;
        this._borderWidth = config.borderWidth || 0;
        this._borderSide = config.borderSide ?? BORDER_SIDE_FULL;
        this._borderOpa = config.borderOpa ?? BORDER_OPA_COVER;
        // Unset stays unset: outlineGeometry.ts applies LVGL's own defaults
        // (opa -> LV_OPA_COVER, colour -> black), so no default is duplicated here.
        this._outlineWidth = config.outlineWidth ?? null;
        this._outlineColor = config.outlineColor ?? null;
        this._outlineOpa = config.outlineOpa ?? null;
        this._outlinePad = config.outlinePad ?? null;
        this._outlineOnly = config.outlineOnly === true;
        this._drawOutlineEnabled = config.drawOutline !== false || this._outlineOnly;

        // Gradient vectors use the object's local width/height. Keep the
        // endpoint current when widgets resize the shared rect directly.
        this.on('widthChange heightChange', () => this._updateBackgroundGradient());

        this.sceneFunc((context: Context, shape) => {
            // console.log("CustomRect sceneFunc called "+ shape);
            const width = this.width();
            const height = this.height();

            // Outline first — LVGL draws it last, but with outline_pad >= 0 it
            // never overlaps the background or border, so painting it first is
            // equivalent and keeps the ring behind anything a widget draws on
            // top. It is drawn entirely OUTSIDE this shape's self rect; Konva
            // does not clip a shape to its self rect, and nothing caches these
            // nodes, so the bounds stay the object's own box.
            if (this._drawOutlineEnabled) this._drawOutline(context, width, height);

            // obj renders its outline through a separate sibling after its
            // clipped children. Its outline-only rect must not paint anything
            // else in this scene pass.
            if (this._outlineOnly) return;

            const geom = resolveBorderGeometry({
                width,
                height,
                radius: this._cornerRadius,
                borderWidth: this._borderWidth,
                borderSide: this._borderSide,
            });

            // Draw the filled background (full size with outer radius). geom.rout
            // is the same value the border's outer edge uses — taking it from the
            // geometry helper keeps the two from silently drifting apart.
            if (this.fill()) {
                context.beginPath();
                this._drawRoundedRect(context, 0, 0, width, height, geom.rout);
                context.closePath();
                context.fillShape(shape as any);
            }

            // Draw the border INSIDE the shape bounds (LVGL-style): one even-odd
            // fill of the ring, clipped to the outer shape.
            const opacity = Math.max(0, Math.min(this._borderOpa ?? BORDER_OPA_COVER, 255)) / 255;
            if (geom.visible && opacity > 0 && this.stroke()) {
                const ctx = context._context;

                // save()/restore() around the whole border: fillShape() above set
                // globalAlpha to this shape's opacity (tied to bg_color's alpha)
                // and never restored it, so without this the border would silently
                // inherit the background's transparency and disappear whenever
                // bg_opa is 0. Border and background opacity are independent in
                // LVGL/firmware; keep them independent here too. The clip and
                // fillStyle set below must not leak out either.
                ctx.save();

                // Clip to the outer shape. This is required, not cosmetic: on a
                // disabled side the inner rect deliberately extends past the
                // widget (see borderGeometry.ts) and would otherwise be filled.
                context.beginPath();
                this._drawRoundedRect(context, 0, 0, width, height, geom.rout);
                context.closePath();
                ctx.clip();

                ctx.globalAlpha = opacity;
                ctx.fillStyle = this.stroke() as string;

                context.beginPath();
                appendRingPath(context, geom);
                ctx.fill("evenodd");

                ctx.restore();
            }
        });

        // Set up hit function for accurate click detection. Deliberately the
        // full outer shape regardless of border_side — the border is purely
        // visual and must not change what is clickable.
        this.hitFunc((context: Context) => {
            const width = this.width();
            const height = this.height();

            context.beginPath();
            this._drawRoundedRect(context, 0, 0, width, height, effectiveRadius(width, height, this._cornerRadius));
            context.closePath();
            context.fillStrokeShape(this as any);
        });
    }

    /**
     * Paint the LVGL outline: ONE closed stroke of the ring's centerline.
     *
     * A single stroke of a single closed path paints every pixel exactly once,
     * so a translucent outline stays uniform at the corners — the reason the
     * border's even-odd ring fill (which exists only to support border_side) is
     * deliberately not reused here.
     *
     * Drawn through the raw 2D context, never Konva's stroke/strokeWidth
     * attributes: those would make Konva inflate the node's bounding box and the
     * outline would silently become the selection/Transformer geometry.
     *
     * globalAlpha is set from outline_opa alone, exactly as the border does
     * below: fillShape() ties globalAlpha to this shape's opacity (which carries
     * bg_color's alpha), and in LVGL outline_opa is independent of bg_opa.
     */
    private _drawOutline(context: Context, width: number, height: number) {
        const geom = resolveOutlineGeometry({
            width,
            height,
            radius: this._cornerRadius,
            outlineWidth: this._outlineWidth,
            outlinePad: this._outlinePad,
            opa: this._outlineOpa,
        });
        if (!geom.visible) return;

        const ctx = context._context;
        ctx.save();
        ctx.globalAlpha = geom.opa / 255;
        // An unset colour resolves to LVGL's own default (black) rather than
        // skipping the draw — on the device `outline_width: 4` alone is visible.
        ctx.strokeStyle = this._outlineColor || OUTLINE_COLOR_DEFAULT;
        ctx.lineWidth = geom.strokeWidth;

        context.beginPath();
        appendOutlinePath(context, geom);
        ctx.stroke();

        ctx.restore();
    }

    private _drawRoundedRect(
        context: Context,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number
    ) {
        // Delegates to the shared path builder so the background edge and the
        // border's outer edge are the same path, not two copies of one.
        appendRoundedRect(context, x, y, width, height, radius);
    }

    /** Apply resolved openHASP background values using Konva's native fill. */
    setBackgroundGradient(
        axis: CanvasGradientAxis,
        mainColor: string,
        gradientColor: string | undefined,
        mainStop: number,
        gradientStop: number,
    ): this {
        this._backgroundGradientConfig = { axis, mainColor, gradientColor, mainStop, gradientStop };
        this._updateBackgroundGradient();
        return this;
    }

    private _updateBackgroundGradient(): void {
        const config = this._backgroundGradientConfig;
        if (!config) return;
        const resolved = resolveBackgroundGradient(
            config.axis,
            config.mainColor,
            config.gradientColor,
            config.mainStop,
            config.gradientStop,
            this.width(),
            this.height(),
        );
        this.fillPriority(resolved.fillPriority);

        if (resolved.active) {
            this.fillLinearGradientColorStops(resolved.colorStops!);
            this.fillLinearGradientStartPoint(resolved.startPoint!);
            this.fillLinearGradientEndPoint(resolved.endPoint!);
        } else {
            // Do not leave an old gradient available as a fallback when the
            // firmware direction is NONE; bg_color is the only active fill.
            this.setAttr('fillLinearGradientColorStops', null);
        }
    }

    /** Konva-style getter/setter for cornerRadius */
    cornerRadius(newValue?: number): number | this {
        if (newValue === undefined) return this._cornerRadius;
        this._cornerRadius = newValue;
        this._redrawCustomRect();
        return this;
    }

    /** Konva-style getter/setter for borderWidth */
    borderWidth(newValue?: number): number | this {
        if (newValue === undefined) return this._borderWidth;
        this._borderWidth = newValue;
        this._redrawCustomRect();
        return this;
    }

    /**
     * Konva-style getter/setter for the LVGL border_side bitmask
     * (0=none, 1=bottom, 2=top, 4=left, 8=right, 15=full).
     * Pass null to reset to FULL — undefined reads the value, per the
     * getter/setter convention the other accessors here use.
     */
    borderSide(newValue?: number | null): number | this {
        if (newValue === undefined) return this._borderSide;
        this._borderSide = newValue ?? BORDER_SIDE_FULL;
        this._redrawCustomRect();
        return this;
    }

    /** Konva-style getter/setter for the LVGL border_opa (0-255). Pass null to reset to fully opaque. */
    borderOpa(newValue?: number | null): number | this {
        if (newValue === undefined) return this._borderOpa;
        this._borderOpa = newValue ?? BORDER_OPA_COVER;
        this._redrawCustomRect();
        return this;
    }

    /**
     * Merge resolved LVGL outline values. The single entry point, mirroring
     * LvglShadowShape.setShadowParams(): callers push whatever the style
     * cascade resolved (including `undefined` for unset), so no defaults and no
     * widget-specific property lists exist at this level. `cornerRadius` is
     * accepted here too because the outline's corners are derived from the
     * object's radius and the two must never be updated apart.
     */
    setOutlineParams(params: {
        outlineWidth?: number | null;
        outlineColor?: string | null;
        outlineOpa?: number | null;
        outlinePad?: number | null;
        cornerRadius?: number | null;
    }): this {
        let changed = false;
        const assign = (key: '_outlineWidth' | '_outlineColor' | '_outlineOpa' | '_outlinePad', value: any) => {
            const next = value ?? null;
            if (this[key] !== next) {
                (this as any)[key] = next;
                changed = true;
            }
        };
        if ('outlineWidth' in params) assign('_outlineWidth', params.outlineWidth);
        if ('outlineColor' in params) assign('_outlineColor', params.outlineColor);
        if ('outlineOpa' in params) assign('_outlineOpa', params.outlineOpa);
        if ('outlinePad' in params) assign('_outlinePad', params.outlinePad);
        if ('cornerRadius' in params) {
            const next = Math.round(Number(params.cornerRadius) || 0);
            if (this._cornerRadius !== next) {
                this._cornerRadius = next;
                changed = true;
            }
        }
        if (changed) this._redrawCustomRect();
        return this;
    }

    /** Current outline parameter snapshot — for tests and debugging. */
    getOutlineParams() {
        return {
            outlineWidth: this._outlineWidth,
            outlineColor: this._outlineColor,
            outlineOpa: this._outlineOpa,
            outlinePad: this._outlinePad,
        };
    }

    /** Set border width via strokeWidth for compatibility with existing code */
    setStrokeWidth(value: number): this {
        this._borderWidth = value;
        this._redrawCustomRect();
        return this;
    }

    /** Get border width */
    getStrokeWidth(): number {
        return this._borderWidth;
    }

    /** Override getSelfRect to return exact bounds without stroke offset */
    getSelfRect() {
        return {
            x: 0,
            y: 0,
            width: this.width(),
            height: this.height()
        };
    }

    /** Override getClientRect to ensure transformer uses exact bounds */
    getClientRect(config?: any) {
        // Get the base client rect from the parent
        const rect = super.getClientRect(config);

        // If we have a relativeTo stage, return the transformed rect at exact size
        if (config && config.skipTransform) {
            return {
                x: 0,
                y: 0,
                width: this.width(),
                height: this.height()
            };
        }

        // Return the exact transformed bounds
        return {
            x: rect.x,
            y: rect.y,
            width: this.width() * this.scaleX(),
            height: this.height() * this.scaleY()
        };
    }

    setAttrs(config: any): this {
        super.setAttrs(config);

        if ('cornerRadius' in config) {
            this._cornerRadius = config.cornerRadius;
        }

        if ('borderWidth' in config) {
            this._borderWidth = config.borderWidth;
        }

        if ('strokeWidth' in config) {
            this._borderWidth = config.strokeWidth;
        }

        if ('borderSide' in config) {
            this._borderSide = config.borderSide ?? BORDER_SIDE_FULL;
        }

        if ('borderOpa' in config) {
            this._borderOpa = config.borderOpa ?? BORDER_OPA_COVER;
        }

        for (const key of ['outlineWidth', 'outlineColor', 'outlineOpa', 'outlinePad'] as const) {
            if (key in config) {
                (this as any)['_' + key] = config[key] ?? null;
            }
        }

        if ('drawOutline' in config) {
            this._drawOutlineEnabled = config.drawOutline !== false || this._outlineOnly;
        }

        this._redrawCustomRect();
        return this;
    }

    /** Request redraw of the shape */
    private _redrawCustomRect(): void {
        // console.trace("CustomRect redraw requested");
        // Clear any cached rendering
        this._clearCache();
        // Request the layer to redraw
        const layer = this.getLayer();
        if (layer) {
            layer.batchDraw();
        }
    }
}
